import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { contactInfo } from '../mockData';
import logo from '../assets/logo.svg';
import { NAV_ITEMS } from '../lib/navigation';

const NAV_LINK_CLASSES = 'text-white hover:text-[#F5F0E8] transition-colors duration-300 text-sm';
const CONTACT_ICON_CLASSES = 'hidden sm:flex items-center text-white hover:text-[#F5F0E8] transition-colors duration-300';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const MotionRouterLink = motion(Link);

  const headerAnimationProps = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: -28 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
      };

  const hoverLift = shouldReduceMotion
    ? {}
    : {
        whileHover: { y: -2 },
        whileTap: { scale: 0.96 },
      };

  const contactLinks = [
    {
      href: `tel:${contactInfo.phone.link}`,
      Icon: Phone,
      label: 'Appeler Siriane Belliard-Reynes',
    },
    {
      href: `mailto:${contactInfo.email.address}`,
      Icon: Mail,
      label: 'Envoyer un e-mail',
    },
  ];

  const toggleMenu = () => setIsMenuOpen((open) => !open);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <motion.header className="bg-[#7D8471] shadow-md w-full" {...headerAnimationProps}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <MotionRouterLink
            to="/"
            onClick={closeMenu}
            className="flex items-center gap-3 flex-shrink-0 text-left no-underline transition-opacity duration-200 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white"
            aria-label="Revenir à l'accueil"
            {...hoverLift}
          >
            <motion.img
              src={logo}
              alt="Monogramme provisoire Siriane Belliard-Reynes (également fourni dans le fichier favicon.ico)"
              className="h-12 w-12 sm:h-14 sm:w-14"
              initial={shouldReduceMotion ? undefined : { rotate: -8, scale: 0.9, opacity: 0 }}
              animate={shouldReduceMotion ? undefined : { rotate: 0, scale: 1, opacity: 1 }}
              transition={shouldReduceMotion ? undefined : { duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
              className="flex-shrink-0 text-left"
              {...(shouldReduceMotion
                ? {}
                : {
                    initial: { opacity: 0, y: 8 },
                    animate: { opacity: 1, y: 0 },
                    transition: { delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                  })}
            >
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-serif text-white">Siriane Belliard-Reynes</h1>
              <p className="text-xs text-[#F5F0E8] mt-0.5">Neuro Thérapeute</p>
            </motion.div>
          </MotionRouterLink>

          <nav className="hidden lg:flex items-center space-x-6">
            {NAV_ITEMS.map(({ href, label }) => (
              <motion.a key={href} href={href} className={NAV_LINK_CLASSES} {...hoverLift}>
                {label}
              </motion.a>
            ))}
          </nav>

          <div className="flex items-center space-x-3">
            {contactLinks.map(({ href, Icon, label }) => (
              <motion.a key={href} href={href} aria-label={label} className={CONTACT_ICON_CLASSES} whileHover={shouldReduceMotion ? undefined : { scale: 1.1 }} whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}>
                <Icon size={18} />
              </motion.a>
            ))}
            <button
              className="lg:hidden text-white p-1"
              onClick={toggleMenu}
              aria-label="Basculer le menu de navigation"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              className="lg:hidden mt-4 pb-4 border-t border-white/20 pt-4"
              initial={shouldReduceMotion ? undefined : { height: 0, opacity: 0 }}
              animate={shouldReduceMotion ? undefined : { height: 'auto', opacity: 1 }}
              exit={shouldReduceMotion ? undefined : { height: 0, opacity: 0 }}
              transition={shouldReduceMotion ? undefined : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <div className="flex flex-col space-y-3">
                {NAV_ITEMS.map(({ href, label }) => (
                  <motion.a key={href} href={href} className={NAV_LINK_CLASSES} onClick={closeMenu} {...hoverLift}>
                    {label}
                  </motion.a>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
